import React, { useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from '../../context/TourContext';
import { onboardingTourSteps } from './onboardingTourSteps';

const OnboardingTour: React.FC = () => {
  const { run, stepIndex, setStepIndex, stopTour, tourKey } = useTour();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (run) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.touchAction = 'none';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Escape' && !e.ctrlKey && !e.metaKey && !['Tab', 'Escape'].includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      const handleWheel = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };
      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };
      const handleScroll = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        window.scrollTo(0, scrollY);
        return false;
      };
      document.addEventListener('keydown', handleKeyDown, { passive: false, capture: true });
      document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
      document.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
      window.addEventListener('scroll', handleScroll, { passive: false, capture: true });
      return () => {
        const savedScrollY = document.body.style.top;
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.touchAction = '';
        if (savedScrollY) {
          const scrollPosition = parseInt(savedScrollY.replace('-', '')) || scrollY;
          window.scrollTo(0, scrollPosition);
        }
        document.removeEventListener('keydown', handleKeyDown, { capture: true } as any);
        document.removeEventListener('wheel', handleWheel, { capture: true } as any);
        document.removeEventListener('touchmove', handleTouchMove, { capture: true } as any);
        window.removeEventListener('scroll', handleScroll, { capture: true } as any);
      };
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.touchAction = '';
    }
  }, [run]);

  const stepRoutes = [
    '/dashboard', '/dashboard', '/dashboard', '/campaigns', '/leads', '/inbound-calls',
    '/analytics', '/credits', '/subscription', '/feedback', '/support', '/settings',
  ];
  const sidebarTourKeys = [
    '', '', 'dashboard', 'campaigns', 'leads', 'inbound-calls', 'analytics', 'credits',
    'subscription', 'feedback', 'support', 'settings',
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type, action, index } = data;
    if (action === ACTIONS.NEXT || action === ACTIONS.PREV) {
      setStepIndex(index);
    }
    if (type === EVENTS.STEP_BEFORE || type === EVENTS.STEP_AFTER) {
      document.querySelectorAll('[data-tour-active="true"]').forEach(el => {
        el.removeAttribute('data-tour-active');
        (el as HTMLElement).style.zIndex = '';
      });
      const pageContent = document.querySelector('main[data-tour="page-content"]');
      const sidebarContainer = document.querySelector('[data-tour="sidebar"]');
      if (pageContent) {
        pageContent.removeAttribute('data-tour-active');
        (pageContent as HTMLElement).style.zIndex = '';
      }
      if (sidebarContainer) {
        sidebarContainer.removeAttribute('data-tour-active');
        (sidebarContainer as HTMLElement).style.zIndex = '';
      }
      if (index === 1 && sidebarContainer) {
        sidebarContainer.setAttribute('data-tour-active', 'true');
        (sidebarContainer as HTMLElement).style.zIndex = '10001';
      }
      if (index >= 2 && sidebarTourKeys[index]) {
        const sidebarLink = document.querySelector(`[data-tour="${sidebarTourKeys[index]}"]`);
        if (sidebarLink) {
          sidebarLink.setAttribute('data-tour-active', 'true');
          (sidebarLink as HTMLElement).style.zIndex = '10001';
        }
        if (pageContent) {
          pageContent.setAttribute('data-tour-active', 'true');
          (pageContent as HTMLElement).style.zIndex = '10001';
        }
        if (sidebarContainer) {
          sidebarContainer.setAttribute('data-tour-active', 'true');
          (sidebarContainer as HTMLElement).style.zIndex = '10001';
        }
      }
    }
    if (type === EVENTS.STEP_BEFORE && index >= 2 && index < stepRoutes.length) {
      const targetPath = stepRoutes[index];
      if (targetPath && location.pathname !== targetPath) {
        navigate(targetPath);
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 100);
      }
    }
    if (type === EVENTS.TARGET_NOT_FOUND && index >= 2 && index < stepRoutes.length) {
      const targetPath = stepRoutes[index];
      if (targetPath && location.pathname !== targetPath) navigate(targetPath);
    }
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      document.querySelectorAll('[data-tour-active="true"]').forEach(el => el.removeAttribute('data-tour-active'));
      stopTour();
    }
  };

  const joyrideStyles = {
    options: {
      primaryColor: '#06b6d4',
      textColor: '#ffffff',
      backgroundColor: '#1e293b',
      overlayColor: 'rgba(0, 0, 0, 0.4)',
      arrowColor: '#1e293b',
      zIndex: 10000,
      spotlightPadding: 0,
    },
    tooltip: { borderRadius: 8, padding: 20 },
    tooltipContainer: { textAlign: 'left' as const },
    buttonNext: { backgroundColor: '#06b6d4', color: '#ffffff', borderRadius: 6, padding: '8px 16px', fontSize: '14px', fontWeight: 600 },
    buttonBack: { color: '#94a3b8', marginRight: 10, fontSize: '14px' },
    buttonSkip: { color: '#94a3b8', fontSize: '14px' },
    beaconInner: { backgroundColor: '#06b6d4' },
    beaconOuter: { borderColor: '#06b6d4' },
  };

  return (
    <Joyride
      key={`tour-${tourKey}`}
      steps={onboardingTourSteps}
      run={run}
      continuous={true}
      showProgress={true}
      showSkipButton={true}
      callback={handleJoyrideCallback}
      styles={joyrideStyles}
      locale={{ back: 'Back', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip Tour' }}
      floaterProps={{ disableAnimation: false }}
      disableOverlayClose={true}
      disableScrolling={true}
      spotlightClicks={false}
      disableScrollParentFix={true}
      scrollOffset={0}
      scrollToFirstStep={false}
      hideCloseButton={true}
    />
  );
};

export default OnboardingTour;
